// Landed cost detail: one drafted cost with its freight/duty components
// and the post action. Posting is final — the cost lands in stock value and
// the books gain one balanced entry. There is no void/reverse endpoint, so a
// posted cost stays posted; the confirmation dialog says so.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import { getStockCost, postStockCost, type StockCost } from "@/lib/api";
import { useAccountLabels } from "@/lib/labels";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "posted":
      return "success";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function plainKind(kind: string): string {
  const lower = kind.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function StockCostDetailPage() {
  const { stockCostId } = useParams({ strict: false }) as {
    stockCostId: string;
  };
  const [detail, setDetail] = useState<StockCost | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const accountLabels = useAccountLabels();

  const reload = useCallback(() => {
    if (!stockCostId) return;
    setError(null);
    getStockCost(stockCostId)
      .then(setDetail)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [stockCostId]);

  useEffect(reload, [reload]);

  const post = async () => {
    if (!stockCostId) return;
    setPosting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await postStockCost(stockCostId);
      setNotice(
        `Posted — the stock value is increased and the entry ${result.journal_entry_id} balances the books.`,
      );
      reload();
    } catch (cause) {
      setError(`Post failed: ${toErrorMessage(cause)}`);
    } finally {
      setPosting(false);
    }
  };

  const confirmPost = () => {
    setConfirming(false);
    void post();
  };

  const status = detail?.status.toLowerCase() ?? "";
  const currency = detail?.currency_code || "USD";

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/stock-costs"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Landed costs
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {detail?.stock_cost_number ?? stockCostId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {detail === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : detail ? (
        <>
          <PageHeader
            title={detail.stock_cost_number}
            description={
              <>
                {detail.description} ·{" "}
                {new Date(detail.cost_date).toLocaleDateString()} ·{" "}
                <StatusDot
                  tone={statusTone(detail.status)}
                  label={plainStatus(detail.status)}
                />
              </>
            }
            actions={
              status === "draft" ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setConfirming(true)}
                  disabled={posting}
                  title="Review and post this cost into stock value (Posted is final — it cannot be posted twice)"
                >
                  {posting ? "Posting…" : "Post into stock"}
                </Button>
              ) : null
            }
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Total into stock
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: detail.total_minor, currency })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Stock account
              </p>
              <p className="mt-1 text-sm">
                {accountLabels.has(detail.inventory_account_id) ? (
                  accountLabels.get(detail.inventory_account_id)
                ) : (
                  <span
                    className="text-[var(--muted-foreground)]"
                    title={detail.inventory_account_id}
                  >
                    Unknown account
                  </span>
                )}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Paid from
              </p>
              <p className="mt-1 text-sm">
                {detail.cash_account_id ? (
                  accountLabels.has(detail.cash_account_id) ? (
                    accountLabels.get(detail.cash_account_id)
                  ) : (
                    <span
                      className="text-[var(--muted-foreground)]"
                      title={detail.cash_account_id}
                    >
                      Unknown account
                    </span>
                  )
                ) : (
                  <span className="text-[var(--muted-foreground)]">
                    Unpaid — sits in what you owe
                  </span>
                )}
              </p>
              {detail.receipt_number ? (
                <p className="mt-2 text-xs text-[var(--muted-foreground)]">
                  Delivery note: {detail.receipt_number}
                </p>
              ) : null}
            </div>
          </div>

          <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kind</TableHead>
                    <TableHead>What</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.components.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        This landed cost has no components.
                      </TableCell>
                    </TableRow>
                  ) : (
                    detail.components.map((component) => (
                      <TableRow key={component.id}>
                        <TableCell>{plainKind(component.kind)}</TableCell>
                        <TableCell>{component.description}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney({
                            cents: component.amount_minor,
                            currency,
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="stack-list grid gap-3 min-[64rem]:hidden">
            {detail.components.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                This landed cost has no components.
              </p>
            ) : (
              detail.components.map((component) => (
                <article
                  key={component.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title">
                      {component.description}
                    </span>
                    <span className="tabular-nums">
                      {formatMoney({
                        cents: component.amount_minor,
                        currency,
                      })}
                    </span>
                  </div>
                  <CardField label="Kind">
                    {plainKind(component.kind)}
                  </CardField>
                </article>
              ))
            )}
          </div>
        </>
      ) : null}

      <Dialog
        open={confirming && detail !== null}
        onOpenChange={(open) => {
          if (!open && !posting) setConfirming(false);
        }}
        title={
          detail
            ? `Post this landed cost of ${formatMoney({
                cents: detail.total_minor,
                currency,
              })} into stock?`
            : ""
        }
        description="Confirming posts the landed-cost journal — stock value up, and what you owe or cash down. Posted is final: it cannot be posted twice."
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirming(false)}
            disabled={posting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={confirmPost} disabled={posting}>
            {posting ? "Posting…" : "Post into stock"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
